import React, { useCallback } from 'react'
import { Helmet } from 'react-helmet'
import { selectLocale } from '../../store/navigation/navigation.selectors'
import logo from '../branding/Mappy-310.png'
import translations from './MetaTags.json'

import { useSelector } from 'react-redux'
import { selectCurrentHistoryFullRoute } from '../../store/history/history.selectors'
import { getHrefLangBySubdomain, SUBDOMAIN_EN, SUBDOMAINS } from '../i18n'
import { isAbsoluteUrl } from '../utils/url'
import { generateAbsoluteBaseUrl } from './seo'

const getImageUrl = ({ image, locale, logo }) => {
  const img = image || logo
  if (!isAbsoluteUrl(img)) return `${generateAbsoluteBaseUrl(locale)}${img}`
  if (img.startsWith('//')) return `https:${img}`
  return img
}

const getAlternateLinks = ({ alternateLinkPath, shouldIgnoreEn }) =>
  SUBDOMAINS.map((subdomain, idx) => {
    // not returning an empty React fragment here `<></>` so returning array
    // is not filling alternate links with irrelevant and undefined components
    if (subdomain === SUBDOMAIN_EN && shouldIgnoreEn) return undefined

    return (
      <link
        key={idx}
        rel="alternate"
        href={`https://${subdomain}.mappy.com${alternateLinkPath}`}
        // eslint-disable-next-line react/no-unknown-property
        hreflang={getHrefLangBySubdomain(subdomain)}
      />
    )
  })

const MetaTags = ({
  title: givenTitle,
  description: givenDescription,
  image,
  url,
  alternateLinkPath,
  canonical,
  type,
  shouldIgnoreEn
}) => {
  const locale = useSelector(selectLocale)
  const currentFullRoute = useSelector(selectCurrentHistoryFullRoute)

  const { title: defaultTitle, description: defaultDescription } = translations?.[locale] ?? {}
  const title = givenTitle || defaultTitle
  const description = givenDescription || defaultDescription
  const img = getImageUrl({ locale, image, logo })
  const typ = type || 'website'

  const buildCanonicalRoute = useCallback(() => {
    const canonicalUrl = !canonical ? currentFullRoute : canonical

    return canonicalUrl.split('#')[0]
  }, [currentFullRoute])

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:locale" content={locale.toLowerCase()} />
      {url && <meta property="og:url" content={url} />}
      <meta property="og:type" content={typ} />
      <meta property="og:image" content={img} />
      {alternateLinkPath && getAlternateLinks({ alternateLinkPath, shouldIgnoreEn })}
      <link rel="canonical" href={`${generateAbsoluteBaseUrl(locale)}${buildCanonicalRoute()}`} />
    </Helmet>
  )
}

export default MetaTags
